import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { api, apiErrorMessage } from "@/lib/api";
import { ShieldCheck, Trash2, Loader2 } from "lucide-react";

const STATUSES = ["new", "in_review", "completed", "rejected"];

const STATUS_STYLES = {
  new: "border-[hsl(var(--status-pending))]/40 bg-[hsl(var(--status-pending))]/10 text-[hsl(var(--status-pending))]",
  in_review: "border-primary/40 bg-primary/10 text-primary",
  completed: "border-[hsl(var(--status-verified))]/40 bg-[hsl(var(--status-verified))]/10 text-[hsl(var(--status-verified))]",
  rejected: "border-destructive/40 bg-destructive/10 text-destructive",
};

export const AdminPrivacyTab = () => {
  const [requests, setRequests] = useState(null);
  const [typeLabels, setTypeLabels] = useState({});
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/privacy-requests");
      setRequests(data.requests);
      setTypeLabels(data.typeLabels || {});
    } catch (error) {
      toast.error(apiErrorMessage(error, "Failed to load privacy requests."));
      setRequests([]);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const updateStatus = async (requestId, status) => {
    try {
      await api.patch(`/admin/privacy-requests/${requestId}`, { status });
      toast.success(`Request marked ${status.replace("_", " ")}.`);
      load();
    } catch (error) {
      toast.error(apiErrorMessage(error, "Status update failed."));
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget?.matched_user_id) return;
    setDeleting(true);
    try {
      const { data } = await api.delete(`/admin/users/${deleteTarget.matched_user_id}`);
      toast.success(data.message || "User data deleted.", { duration: 9000 });
      setDeleteTarget(null);
      load();
    } catch (error) {
      toast.error(apiErrorMessage(error, "Deletion failed."), { duration: 9000 });
    } finally {
      setDeleting(false);
    }
  };

  return (
    <Card className="bg-card border-border">
      <CardHeader className="pb-3">
        <CardTitle className="font-display text-lg flex items-center gap-2">
          <ShieldCheck className="h-5 w-5 text-primary" /> Privacy requests
        </CardTitle>
        <CardDescription>
          Requests submitted from the public privacy form and member opt-outs. Deletion permanently removes
          the member's account, cases, uploads, tickets, and notifications — the audit trail is retained as
          the legal record.
        </CardDescription>
      </CardHeader>
      <CardContent>
        {requests === null ? (
          <div className="space-y-2">
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-10 w-full" />
          </div>
        ) : requests.length === 0 ? (
          <p className="text-sm text-muted-foreground py-6 text-center" data-testid="privacy-requests-empty">
            No privacy requests yet. Requests from the public form and member opt-outs will appear here.
          </p>
        ) : (
          <Table data-testid="privacy-requests-table">
            <TableHeader>
              <TableRow>
                <TableHead>Received</TableHead>
                <TableHead>Requester</TableHead>
                <TableHead>Type</TableHead>
                <TableHead>Details</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {requests.map((request) => (
                <TableRow key={request.id} data-testid={`privacy-request-row-${request.id}`}>
                  <TableCell className="text-xs text-muted-foreground whitespace-nowrap">
                    {request.created_at ? new Date(request.created_at).toLocaleDateString() : ""}
                  </TableCell>
                  <TableCell>
                    <span className="block text-sm">{request.name}</span>
                    <span className="block text-xs text-muted-foreground font-mono">{request.email}</span>
                  </TableCell>
                  <TableCell className="text-xs max-w-[180px]">
                    {typeLabels[request.request_type] || request.request_type}
                    <span className="block text-[10px] text-muted-foreground uppercase tracking-wide">
                      via {request.source === "dashboard" ? "member dashboard" : "public form"}
                    </span>
                  </TableCell>
                  <TableCell className="text-xs text-muted-foreground max-w-[220px] truncate" title={request.details}>
                    {request.details || "—"}
                  </TableCell>
                  <TableCell>
                    <span
                      className={`inline-flex rounded border px-2 py-0.5 text-[11px] font-medium capitalize ${STATUS_STYLES[request.status] || "border-border text-muted-foreground"}`}
                      data-testid={`privacy-status-badge-${request.id}`}
                    >
                      {request.status.replace("_", " ")}
                    </span>
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex items-center justify-end gap-2">
                      <Select value={request.status} onValueChange={(status) => updateStatus(request.id, status)}>
                        <SelectTrigger className="h-8 w-32" data-testid={`privacy-status-select-${request.id}`}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {STATUSES.map((status) => (
                            <SelectItem key={status} value={status}>
                              {status.replace("_", " ")}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <Button
                        size="sm"
                        variant="destructive"
                        disabled={!request.matched_user_id}
                        title={request.matched_user_id ? "Delete this user's account and data" : "No registered account matches this email"}
                        onClick={() => setDeleteTarget(request)}
                        data-testid={`privacy-delete-user-button-${request.id}`}
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}

        <AlertDialog open={Boolean(deleteTarget)} onOpenChange={(open) => !open && setDeleteTarget(null)}>
          <AlertDialogContent data-testid="privacy-delete-dialog">
            <AlertDialogHeader>
              <AlertDialogTitle>Delete this member's account and all data?</AlertDialogTitle>
              <AlertDialogDescription>
                This permanently removes the account for{" "}
                <span className="font-mono text-foreground">{deleteTarget?.email}</span> including all cases,
                uploaded documents, reports, tickets, and notifications. This cannot be undone. The audit
                trail is kept as the legal record of the deletion.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel data-testid="privacy-delete-cancel">Cancel</AlertDialogCancel>
              <AlertDialogAction
                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                onClick={confirmDelete}
                data-testid="privacy-delete-confirm"
              >
                {deleting ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : null}
                Yes, delete everything
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </CardContent>
    </Card>
  );
};
